'use client';

import { useId } from '@react-aria/utils';
import { useControlledState } from '@react-stately/utils';
import clsx from 'clsx';
import { type ReactNode, type ForwardedRef, useMemo, useContext } from 'react';
import { Link as RACLink, type LinkProps as RACLinkProps } from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import type {
  AriaLabelingProps,
  CommonProps,
  Href,
  NavigationProps,
  StringLikeChildren,
  WithRequired,
} from '../types.js';
import { Text } from '../typography/text.js';
import { LinkTabsContext, type LinkTabsContextValue } from './context.js';

export interface LinkTabsProps extends CommonProps, AriaLabelingProps {
  /** Link tabs belonging to the group. */
  children: ReactNode;
  /** Whether the link tabs are disabled. */
  isDisabled?: boolean;
  /** The currently selected key in the collection (controlled). */
  currentHref?: Href | null;
  /** The initial selected key in the collection (uncontrolled). */
  defaultHref?: Href | null;
  /** Handler that is called when the selection changes. */
  onHrefChange?: (href: Href | null) => void;
}

function LinkTabs(
  {
    children,
    currentHref,
    defaultHref,
    onHrefChange,
    UNSAFE_className,
    UNSAFE_style,
    id,
    isDisabled,
    ...props
  }: LinkTabsProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy } = props;

  useProductionWarning(() => {
    if (!ariaLabel && !ariaLabelledBy) {
      console.warn('LinkTabs requires one of aria-label / aria-labelledby for accessibility');
    }
  }, [ariaLabel, ariaLabelledBy]);

  const linkTabsId = useId(id);
  const [href, setHref] = useControlledState(currentHref, defaultHref ?? null, onHrefChange);

  const linkTabsContext: LinkTabsContextValue = useMemo(
    () => ({
      isDisabled,
      currentHref: href,
      setCurrentHref: setHref,
    }),
    [isDisabled, href, setHref],
  );

  return (
    <nav {...props} ref={ref} id={linkTabsId} className={clsx('cim-link-tabs', UNSAFE_className)}>
      <ul style={UNSAFE_style} aria-labelledby={linkTabsId} data-disabled={isDisabled} className="cim-tab-list">
        <LinkTabsContext.Provider value={linkTabsContext}>{children}</LinkTabsContext.Provider>
      </ul>
    </nav>
  );
}

/**
 * Displays a list of tabs to help users navigate through a website.
 *
 * See [tabs usage guidelines](https://ui.cimpress.io/components/tabs/).
 */
const _LinkTabs = withStyleProps(forwardRef(LinkTabs), 'LinkTabs');

export { _LinkTabs as LinkTabs };

export interface LinkTabProps
  extends
    Omit<CommonProps, 'id'>,
    WithRequired<NavigationProps, 'href'>,
    Pick<RACLinkProps, 'children' | 'isDisabled' | 'onHoverStart' | 'onHoverEnd'> {
  /** The text to display as the tab title. */
  children: StringLikeChildren;
  /** An icon displayed before the tab title. */
  iconStart?: ReactNode;
  /** A badge displayed after the tab title. */
  badge?: ReactNode;
}

/** Renders a single tab within `LinkTabs`. */
export function LinkTab({
  children,
  href,
  iconStart,
  badge,
  UNSAFE_className,
  UNSAFE_style,
  isDisabled,
  ...props
}: LinkTabProps) {
  const linkTabsContext = useContext(LinkTabsContext);
  if (!linkTabsContext) {
    throw new Error('Cannot use link tab outside of link tabs');
  }

  const { isDisabled: isGroupDisabled, currentHref, setCurrentHref } = linkTabsContext;

  const isCurrent = href === currentHref;

  return (
    <li>
      <RACLink
        {...props}
        className={clsx('cim-tab-item', 'cim-link-tab-item', UNSAFE_className)}
        style={UNSAFE_style}
        href={href}
        onPress={() => setCurrentHref(href)}
        aria-current={isCurrent ? 'page' : undefined}
        data-selected={isCurrent || undefined}
        isDisabled={isDisabled || isGroupDisabled}
      >
        {iconStart}
        <Text as="span" variant="medium-semibold" alignment="center">
          {children}
        </Text>
        {badge}
      </RACLink>
    </li>
  );
}

LinkTab.displayName = 'LinkTab';
